import { useMemo } from "react";
import { checkUserPermissionFunc } from "../components/common/CommonFunctions";

// Real session-backed role detection for the appraisal module. Reads from
// sessionStorage (populated by AuthContext on login). A user can hold more
// than one role simultaneously — HR users are typically also RMs, and every
// user is an employee in their own right.
//
//   isHR          → can manage cycles, KPI library, final approvals
//   isRM          → can assign appraisals and review subordinates
//   isEmployee    → can self-evaluate their own appraisals
//
// Each role flag drives a different scope on `GET /appraisal/appraisals/`:
//   mine (employee), team (RM), all (HR).
const useAppraisalRole = () => {
    const role = useMemo(() => {
        const isSuperAdmin = JSON.parse(sessionStorage.getItem("is_super_admin") || "false");
        const isAdmin = JSON.parse(sessionStorage.getItem("is_admin") || "false");
        // The backend serializes the flag with the typo `is_reproting_manager`
        // (see AuthContext). The string survives a JSON.parse cleanly because
        // it's literally the string "true" / "false" in sessionStorage.
        const reportingManagerRaw = sessionStorage.getItem("reportingManager");
        const isReportingManager = reportingManagerRaw === "true" || reportingManagerRaw === true;
        const userId = JSON.parse(sessionStorage.getItem("userId") || "null");
        const employeeId = sessionStorage.getItem("employee_id");
        const employeeName = sessionStorage.getItem("employee_name") || "";
        const designation = sessionStorage.getItem("designation") || "";
        const department = sessionStorage.getItem("department") || "";

        const isHR = !!(isAdmin || isSuperAdmin);
        return {
            isHR,
            isRM: isReportingManager,
            // Anyone authenticated has a personal appraisal view; even an HR
            // user gets their own appraisal data through /appraisals/?scope=mine.
            isEmployee: !!employeeId,
            user: {
                id: userId,
                employee_id: employeeId,
                name: employeeName,
                designation,
                department,
            },
        };
    }, []);

    return role;
};

// Helper for appraisal-specific permission checks. Sub-menu keys match
// AppraisalModule field names (and appraisal/urls.py name= values).
export const useAppraisalPermissions = () => {
    return useMemo(() => {
        const myAppraisal = checkUserPermissionFunc("appraisal", "my_appraisals", "SubMenu");
        const teamReview = checkUserPermissionFunc("appraisal", "team_appraisals", "SubMenu");
        const kpiManager = checkUserPermissionFunc("appraisal", "appraisal_kpis", "SubMenu");
        const cycles = checkUserPermissionFunc("appraisal", "appraisal_cycles", "SubMenu");
        const finalApproval = checkUserPermissionFunc("appraisal", "appraisal_approval", "SubMenu");
        const isAdmin = JSON.parse(sessionStorage.getItem("is_admin") || "false")
            || JSON.parse(sessionStorage.getItem("is_super_admin") || "false");
        const allow = (p) => isAdmin || !!p?.permission;
        return {
            canViewMyAppraisal: allow(myAppraisal),
            canViewTeamReview: allow(teamReview),
            canViewKpiManager: allow(kpiManager),
            canViewCycles: allow(cycles),
            canViewFinalApproval: allow(finalApproval),
            canAddKpi: isAdmin || !!kpiManager?.add,
            canEditKpi: isAdmin || !!kpiManager?.edit,
            canAddCycle: isAdmin || !!cycles?.add,
            canEditCycle: isAdmin || !!cycles?.edit,
            canAssignAppraisal: isAdmin || !!teamReview?.add,
        };
    }, []);
};

export default useAppraisalRole;
